import { Box, TextField } from '@mui/material'
import Modal from '@mui/material/Modal'
import styles from './styles.module.css'
import generateMenuItems from '../../../../global/utils/generate-menu-items'
import {
  ISSUES_STATUSES_ARRAY,
  OPERATING_STATUSES,
  OPERATING_STATUSES_ARRAY,
} from '../../../../global/constants/van'
import { VanContext } from '../../../../global/context/van-context/VanContext'
import { useContext, useEffect, useState } from 'react'
import transformDate from '../../common/transform-date'
import formatDate from '../../common/format-date'
import PrimaryButton from '../../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../../global/components/buttons/secondary-button/SecondaryButton'
import moment from 'moment'
import determineIsDateValid from '../common/determine-is-date-valid'

interface IChangeOperatingStatusModalProps {
  handleClosed: Function
  isOpen: boolean

  setOperatingStatusNote: Function
}

interface IFormData {
  operatingStatus: string
  operatingStatusStartDate: Date | undefined
  operatingStatusNote: string
  issue?: string
}

const DEFAULT_FORM_DATA: IFormData = {
  operatingStatus: '',
  operatingStatusStartDate: undefined,
  operatingStatusNote: '',
}

function ChangeOperatingStatusModal({
  handleClosed,
  isOpen,
  setOperatingStatusNote,
}: IChangeOperatingStatusModalProps) {
  const { vanData, setVanData, queryVanData } = useContext(VanContext)

  const [formData, setFormData] = useState<IFormData>(DEFAULT_FORM_DATA)
  const [isFormDataValid, setIsFormDataValid] = useState(false)
  const [isIssueRequired, setIsIssueRequired] = useState(false)

  const [operatingStatusMenuItems] = useState(
    generateMenuItems(
      OPERATING_STATUSES_ARRAY.filter(
        (operatingStatus) => operatingStatus !== vanData.operatingStatus,
      ),
    ),
  )
  const [issuesMenuItems] = useState(generateMenuItems(ISSUES_STATUSES_ARRAY))

  useEffect(() => {
    const tempIsIssueRequired =
      formData.operatingStatus === OPERATING_STATUSES.INOPERATIVE
    setIsIssueRequired(tempIsIssueRequired)

    if (!tempIsIssueRequired) {
      setFormData({
        ...formData,
        issue: undefined,
      })
    }

    const isIssueValid = !tempIsIssueRequired || Boolean(formData.issue)
    const isOperatingStatusValid = Boolean(formData.operatingStatus)
    const isOperatingStatusStartDateValid = determineIsDateValid(
      formData.operatingStatusStartDate,
    )

    const tempIsFormDataValid =
      isIssueValid && isOperatingStatusValid && isOperatingStatusStartDateValid
    setIsFormDataValid(tempIsFormDataValid)
  }, [
    formData.operatingStatus,
    formData.issue,
    formData.operatingStatusStartDate,
  ])

  return (
    <Modal
      open={isOpen}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
      onClose={() => handleClosed()}
    >
      <Box className={styles.editVanSubModal}>
        <div className={styles.editVanSubModalPageTitle}>
          Change Operating Status
        </div>
        <div className={styles.editVanSubModalContent}>
          <div className={styles.requiredFieldContainer}>
            <TextField
              type='date'
              label='Operating Status Start Date'
              name='startDate'
              InputLabelProps={{ shrink: true }}
              sx={{ width: '162px', display: '' }}
              InputProps={{
                inputProps: {
                  min: moment(vanData.operatingStatusStartDate).format(
                    'YYYY-MM-DD',
                  ),
                },
              }}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  operatingStatusStartDate: transformDate(
                    e.target.value,
                  ) as Date,
                })
              }
              value={formatDate(formData.operatingStatusStartDate)}
              required
            />
            <TextField
              className={styles.dropDown}
              select
              name='operatingStatus'
              label='Operating Status'
              value={formData.operatingStatus}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  operatingStatus: e.target.value,
                })
              }
              required
            >
              {operatingStatusMenuItems}
            </TextField>
            {isIssueRequired && (
              <TextField
                className={styles.dropDown}
                select
                name='issue'
                label='Issue'
                value={formData.issue}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    issue: e.target.value,
                  })
                }
                required={isIssueRequired}
              >
                {issuesMenuItems}
              </TextField>
            )}
          </div>
          <TextField
            sx={{ width: '100%', height: '100%' }}
            InputLabelProps={{ shrink: true }}
            multiline
            rows={10}
            label='Operating Status Note'
            value={formData.operatingStatusNote}
            disabled={false}
            onChange={(e) =>
              setFormData({
                ...formData,
                operatingStatusNote: e.target.value,
              })
            }
          />
        </div>
        <div className={styles.changeOperatingStatusMessage}>
          *Changes will not take effect until you click "Save Changes" on the
          Van Details Page (click "Change" -{'>'} "Save Changes").
        </div>
        <div className={styles.editVanSubModalButtons}>
          <SecondaryButton
            buttonName='Cancel'
            onClick={async () => {
              const originalVanData = await queryVanData(vanData.objectId)
              const { operatingStatus, operatingStatusStartDate } =
                originalVanData

              setFormData(DEFAULT_FORM_DATA)

              setVanData({
                operatingStatus,
                operatingStatusStartDate,
              })

              handleClosed()
            }}
          />
          <PrimaryButton
            buttonName='Change'
            onClick={() => {
              setOperatingStatusNote(formData.operatingStatusNote)
              setVanData({
                issue: formData.issue,
                operatingStatus: formData.operatingStatus,
                operatingStatusStartDate: formData.operatingStatusStartDate,
              })

              handleClosed()
            }}
            disabled={!isFormDataValid}
          />
        </div>
      </Box>
    </Modal>
  )
}

export default ChangeOperatingStatusModal
